import { Link, useLocation } from 'react-router';
import {
  Container,
  UserContainer,
  Avatar,
  AvatarImage,
  ControllersContainer,
  SidebarMenuItem,
  Logo,
  MenuItemsStack,
  MainItemsStack,
  NavTypography,
  ListItem,
} from '../styles';
import {
  useEffect,
  useMemo,
  useRef,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useTheme } from '@mui/material';
import Typography from '@mui/material/Typography';
import { useAuth } from '@/hooks/useAuth';
import { Moon, Sun, LogOutIcon, EditIcon } from 'lucide-react';
import { useThemeMode } from '@/context/ThemeContext';
import Collapse from '@mui/material/Collapse';
import List from '@mui/material/List';
import ListItemIcon from '@mui/material/ListItemIcon';
import ListItemText from '@mui/material/ListItemText';
import Stack from '@mui/material/Stack';
import Skeleton from '@mui/material/Skeleton';
import { getInitials, stringToColor } from '@/utils/stringUtils';
import { useUser } from '@/context/UserContext';
import { menuItems } from '../menuItems';
import { useTranslation } from 'react-i18next';
import SelectInput from '@/components/Input/SelectInput';
import { useLanguage } from '@/context/LanguageContext';

interface LeftProps {
  expanded: boolean;
  setExpanded: Dispatch<SetStateAction<boolean>>;
  onEditUser: () => void;
}
function Left({ expanded, setExpanded, onEditUser }: LeftProps) {
  const location = useLocation();
  const currentPath = location.pathname;
  const { language, setLanguage } = useLanguage();
  const { toggleTheme, mode } = useThemeMode();
  const theme = useTheme();
  const navRef = useRef<HTMLDivElement>(null);
  const { logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const { user } = useUser();
  const { t } = useTranslation();
  const imageUrlPath = user?.image?.url
    ? new URL(user.image.url).pathname.replace(/^\/+/, '')
    : '';

  const name = user?.name || '';
  const initials = useMemo(() => getInitials(name || ''), [name]);
  const bgColor = useMemo(
    () => stringToColor(name || Math.random().toString()),
    [name]
  );

  const handleSideBar = () => {
    console.log(user);
    onEditUser();
    setExpanded((prev) => !prev);
  };

  //setExpanded(true);
  useEffect(() => {
    console.log('render');
    setLoading(false);
    function handleClickOutside(event: MouseEvent) {
      if (navRef.current && !navRef.current.contains(event.target as Node)) {
        setExpanded(false);
      }
    }

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [setExpanded]);

  const handleLogout = () => {
    logout();
  };

  const [openSubmenu, setOpenSubmenu] = useState<boolean>(false);
  const handleClick = () => {
    setOpenSubmenu(!openSubmenu);
  };

  return (
    <Container
      expanded={expanded ? true : false}
      ref={navRef}
      onMouseOver={() => setExpanded(true)}
      onMouseOut={() => setExpanded(false)}
    >
      <div style={{ marginLeft: expanded ? '24px' : '0px' }}>
        <Logo />
      </div>

      <MenuItemsStack>
        {menuItems(t, user?.role).map(({ path, icon, label, submenu }) => (
          <MainItemsStack key={path} expanded={expanded}>
            {/** Selected button on left navbar */}
            <SidebarMenuItem
              data-active={currentPath === path ? true : false}
              data-expanded={expanded}
              to={path}
              onClick={submenu ? handleClick : undefined}
              onMouseEnter={submenu ? () => setOpenSubmenu(true) : undefined}
            >
              {icon}
              {expanded ? (
                <NavTypography variant="caption">{label}</NavTypography>
              ) : (
                ''
              )}
            </SidebarMenuItem>

            {/**  */}
            {submenu && expanded && (
              <Collapse in={openSubmenu} timeout="auto" unmountOnExit>
                <List
                  sx={{ backgroundColor: theme.palette.white['02'] }}
                  onMouseLeave={
                    submenu ? () => setOpenSubmenu(false) : undefined
                  }
                >
                  {submenu.map((option) => (
                    <Link to={option.path} key={option.path}>
                      <ListItem key={option.path}>
                        <ListItemIcon>{option.icon}</ListItemIcon>
                        <ListItemText>{option.label}</ListItemText>
                      </ListItem>
                    </Link>
                  ))}
                </List>
              </Collapse>
            )}
          </MainItemsStack>
        ))}
      </MenuItemsStack>

      <div style={{ flex: 1 }} />

      <ControllersContainer expanded={expanded}>
        <LogOutIcon onClick={handleLogout} cursor="pointer" />
        <SelectInput
          value={language.toUpperCase()}
          options={['EN', 'PT', 'ES']}
          onChange={(event) => setLanguage(event.target.value)}
          width="64px"
          startIcon={
            language === 'pt' ? (
              <div>🇧🇷</div>
            ) : language === 'en' ? (
              <div>🇬🇧</div>
            ) : (
              <div>🇪🇸</div>
            )
          }
          externalSx={{
            '& .MuiOutlinedInput-root': {
              border: 'none',
              backdropFilter: 'none',
            },
          }}
        />

        {mode === 'dark' ? (
          <Sun onClick={toggleTheme} cursor="pointer" />
        ) : (
          <Moon onClick={toggleTheme} cursor="pointer" />
        )}
      </ControllersContainer>

      <UserContainer onClick={handleSideBar}>
        {loading ? (
          <Skeleton variant="circular" height={48} width={48} />
        ) : (
          <Stack alignItems="center" width="100%">
            <Avatar
              $bgColor={user?.image?.url ? 'transparent' : bgColor}
              $size={48}
            >
              {user?.image?.url ? (
                <AvatarImage
                  src={'/image/' + imageUrlPath} // usa a URL exata do backend
                  alt={user.name || undefined}
                />
              ) : initials && initials.trim() ? (
                initials
              ) : (
                '?'
              )}
            </Avatar>
          </Stack>
        )}
        {expanded && (
          <Stack direction="row" alignItems="center" gap={2}>
            <div style={{ color: 'white', textAlign: 'left' }}>
              <Typography variant="body1">{name}</Typography>
              <Typography variant="body2" fontWeight={600}>
                {t(`config.users.${user?.role}`)}
              </Typography>
            </div>
            <div style={{ lineHeight: 0 }}>
              <EditIcon color="white" strokeWidth={2} size={20} />
            </div>
          </Stack>
        )}
      </UserContainer>
    </Container>
  );
}

export default Left;
